import { SelectItem } from '@wui/common-library'
import { computed, ref, type MaybeRefOrGetter, toValue } from 'vue'

import { Contact } from '@/shared/types/api'

import { useStoreContacts } from '..'

import {
  CONTACT_PARTICIPANTS_SELECT_SEARCH_FIELDS,
  useSearchContactsByStringPaginated,
} from './use-search-contacts-by-string-paginated'

type UseContactParticipantsSelectSearchOptions = {
  excludeInternalNumber?: MaybeRefOrGetter<string | undefined>
}

const resolveContactFromStore = (
  contact: Contact,
  findById: ReturnType<typeof useStoreContacts>['findById'],
  findByInternalNumber: ReturnType<typeof useStoreContacts>['findByInternalNumber'],
  findByPServed: ReturnType<typeof useStoreContacts>['findByPServed'],
): Contact => {
  return findById(contact.id)
    ?? (contact.internalNumber ? findByInternalNumber(contact.internalNumber) : undefined)
    ?? (contact.imLogin ? findByPServed(contact.imLogin) : undefined)
    ?? contact
}

const getParticipantDedupeKey = (contact: Contact): string | undefined => {
  const internalNumber = contact.internalNumber?.trim()
  if (internalNumber) {
    return `num:${internalNumber}`
  }

  const mobilePhone = contact.mobilePhone?.trim()
  if (mobilePhone) {
    return `mob:${mobilePhone}`
  }

  if (contact.id) {
    return `id:${contact.id}`
  }

  if (contact.guid) {
    return `guid:${contact.guid}`
  }

  return contact.imLogin ? `im:${contact.imLogin}` : undefined
}

const pickRicherContact = (left: Contact, right: Contact): Contact => {
  if (left.imLogin && !right.imLogin) {
    return left
  }

  if (right.imLogin && !left.imLogin) {
    return right
  }

  return left
}

const mapContactsToSelectItems = (
  source: SelectItem<Contact>[],
  findById: ReturnType<typeof useStoreContacts>['findById'],
  findByInternalNumber: ReturnType<typeof useStoreContacts>['findByInternalNumber'],
  findByPServed: ReturnType<typeof useStoreContacts>['findByPServed'],
  excludeInternalNumber?: string,
): SelectItem<Contact>[] => {
  const mergedByKey = new Map<string, Contact>()

  for (const item of source) {
    const contact = resolveContactFromStore(
      item.value,
      findById,
      findByInternalNumber,
      findByPServed,
    )

    if (excludeInternalNumber && contact.internalNumber === excludeInternalNumber) {
      continue
    }

    const key = getParticipantDedupeKey(contact)
    if (!key) {
      continue
    }

    const existing = mergedByKey.get(key)
    mergedByKey.set(key, existing ? pickRicherContact(existing, contact) : contact)
  }

  return [...mergedByKey.values()].map((contact) => ({
    label: contact.name,
    value: contact,
  }))
}

export const useContactParticipantsSelectSearch = (
  options: UseContactParticipantsSelectSearchOptions = {},
) => {
  const { string, fetchNext, items, allItems: addressBookItems } = useSearchContactsByStringPaginated()
  const { findById, findByInternalNumber, findByPServed } = useStoreContacts()
  const selectedMember = ref<string[]>([])

  const mapSource = (source: SelectItem<Contact>[] | undefined) => mapContactsToSelectItems(
    source ?? [],
    findById,
    findByInternalNumber,
    findByPServed,
    toValue(options.excludeInternalNumber),
  )

  const participantsItems = computed<SelectItem<Contact>[]>(() => mapSource(items.value))
  const allItems = computed<SelectItem<Contact>[]>(() => mapSource(addressBookItems?.value))

  const selectedNumber = computed(() => {
    const selectedId = selectedMember.value[0]
    const item = participantsItems.value.find((i) => i.value.id === selectedId)
    if (item) {
      const { internalNumber, mobilePhone, imLogin } = item.value
      return internalNumber || mobilePhone || imLogin
    }

    if (selectedId) {
      return selectedId
    }

    return undefined
  })

  return {
    string,
    fetchNext,
    selectedMember,
    participantsItems,
    allItems,
    selectedNumber,
    fieldsSearchBy: CONTACT_PARTICIPANTS_SELECT_SEARCH_FIELDS,
  }
}
